import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useChartStable, useYScale } from "./chart-context";
import { resolveReferenceDataRange } from "./reference-area-geometry";
import { normalizeYAxisId } from "./y-axis-scales";
import {
  resolveYAxisTickCount,
  Y_AXIS_DEFAULT_TICK_COUNT,
} from "./y-axis-ticks";


function formatLabel(value, formatLargeNumbers, formatValue) {
  if (formatValue) {
    return formatValue(value);
  }
  if (formatLargeNumbers && value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return String(value);
}

function resolveTickLabelColor(tickY, axisId, yScale, referenceAreas) {
  for (const area of referenceAreas) {
    if (!area.axisLabelColor) {
      continue;
    }
    if (normalizeYAxisId(area.yAxisId) !== axisId) {
      continue;
    }
    const [low, high] = resolveReferenceDataRange(area.y1, area.y2, yScale.domain());
    const topPixel = yScale(high) ?? 0;
    const bottomPixel = yScale(low) ?? 0;
    const bandTop = Math.min(topPixel, bottomPixel);
    const bandBottom = Math.max(topPixel, bottomPixel);
    if (tickY >= bandTop && tickY <= bandBottom) {
      return area.axisLabelColor;
    }
  }
  return undefined;
}

export function YAxis(props) {
  const { containerRef } = useChartStable();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const container = containerRef.current;
  if (!(mounted && container)) {
    return null;
  }

  return <YAxisInner {...props} container={container} />;
}

const YAxisInner = memo(function YAxisInner({
  yAxisId,
  orientation = "left",
  numTicks = Y_AXIS_DEFAULT_TICK_COUNT,
  formatLargeNumbers = true,
  formatValue,
  // Lodgeic extension: label only the top of the scale — the high-water
  // mark — instead of a full tick ladder. A stack of five money labels on a
  // 120px card is noise; one number states the scale and the tooltip carries
  // exact values. `numTicks` still drives the gridlines.
  peak = false,
  container
}) {
  const { margin, referenceAreas } = useChartStable();
  const yScale = useYScale(yAxisId);
  const isLeft = orientation === "left";
  const axisId = normalizeYAxisId(yAxisId);

  const ticks = useMemo(() => {
    const domain = yScale.domain();
    const domainMax = domain[domain.length - 1];
    const tickValues = peak
      ? // Prefer the topmost "nice" tick over the raw domain max: the domain
        // top can be an un-round number, and the nice tick is also where the
        // top gridline sits, so the label lines up with a rule.
        [
          yScale
            .ticks(resolveYAxisTickCount(numTicks))
            .filter((v) => v <= domainMax)
            .pop() ?? domainMax,
        ]
      : yScale.ticks(resolveYAxisTickCount(numTicks));
    return tickValues.map((value) => {
      const y = (yScale(value) ?? 0) + margin.top;
      return {
        value,
        y,
        label: formatLabel(value, formatLargeNumbers, formatValue),
        labelColor: resolveTickLabelColor(y - margin.top, axisId, yScale, referenceAreas),
      };
    });
  }, [
    yScale,
    margin.top,
    numTicks,
    peak,
    formatLargeNumbers,
    formatValue,
    axisId,
    referenceAreas,
  ]);

  return createPortal(<div className="pointer-events-none absolute inset-0">
    <div
      className="absolute top-0 bottom-0"
      style={
        isLeft
          ? { left: 0, width: margin.left }
          : { right: 0, width: margin.right }
      }>
      {ticks.map((tick) => (
        <div
          className="absolute flex items-center"
          key={tick.value}
          style={{
            top: tick.y,
            transform: "translateY(-50%)",
            // No CSS transition on `top`: the shell already tweens the
            // y-domain frame by frame, so the scale itself supplies the
            // motion. A second, competing 500ms transition could stall
            // mid-flight and strand a label at the wrong height — it hit
            // exactly one tick per chart, the value (usually 0) whose key
            // survives the loading→data domain swap and therefore animates
            // instead of mounting fresh.
            ...(isLeft
              ? { right: 0, justifyContent: "flex-end", paddingRight: 8 }
              : { left: 0, justifyContent: "flex-start", paddingLeft: 8 }),
          }}>
          <span
            className="text-chart-label text-xs"
            style={tick.labelColor ? { color: tick.labelColor } : undefined}>
            {tick.label}
          </span>
        </div>
      ))}
    </div>
  </div>, container);
});

YAxis.displayName = "YAxis";

export default YAxis;
